import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import {
  Alert,
  Button,
  ButtonLink,
  Card,
  DataRow,
  ErrorState,
  OptionCard,
  RadioGroup,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { AddressForm, NEW_ADDRESS } from '@/features/address/AddressForm';
import type { PickedLocation } from '@/features/location/LocationPicker';
import { useSession } from '@/features/auth/sessionContext';
import { PriceBreakdown } from '@/features/pricing/PriceBreakdown';
import { useAnnounceOnChange } from '@/lib/a11y';
import { createAddress, useAddresses } from '@/lib/api/addresses';
import { useApiMutation } from '@/lib/api/queryClient';
import { createQuote, useOptionPrices, useQuote } from '@/lib/api/quotes';
import { documentsFromQuote, otherDocuments } from '@/features/pricing/basket';
import type { AddressOut, FulfilmentMethod, QuoteOut } from '@/lib/api/types';
import { formatPaise } from '@/lib/format';
import type { AddressFormValues } from '@/lib/validators';

import { stepUrl } from './steps';
import { useOrderStep } from './useOrderStep';

const FORM_ID = 'address-form';

interface SubmitResult {
  quote: QuoteOut;
  address: AddressOut;
}

/**
 * /order/address — where it goes, and what that makes the total.
 *
 * The order of operations is the requirement: PIN code, then serviceability,
 * then the delivered total, and only then a pay button. The total on this screen
 * is priced by POST /quotes/options with the PIN code included, which is the
 * same engine that will price the final quote — so the number the customer reads
 * before pressing the button is the number the button commits to.
 *
 * Pressing Continue does two writes in order: save the address (it is the only
 * durable handle the payment step will have on it) and create the quote that
 * carries the PIN code. The new quote id replaces `?q=` so the flow stays
 * recoverable from the URL alone.
 */
export function AddressStep() {
  const { step, stepper, urlState } = useOrderStep();
  const navigate = useNavigate();
  const session = useSession();

  const quote = useQuote(urlState.quote);
  const [pincode, setPincode] = useState<string | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<string>(NEW_ADDRESS);
  //  Component state, not the URL: a coordinate is not progress the customer
  //  can lose without noticing, and a delivery address in a shareable link is
  //  the one thing that should not be in one.
  const [location, setLocation] = useState<PickedLocation | null>(null);

  // Delivery or collection. Seeded from the quote so a refresh — or coming back
  // from Pay — keeps the choice, then held here because changing it re-prices
  // and only the Continue button commits that.
  const [method, setMethod] = useState<FulfilmentMethod | null>(null);
  const fulfilmentMethod = method ?? quote.data?.fulfilment_method ?? 'delivery';
  const isPickup = fulfilmentMethod === 'pickup';

  // The whole basket, so the delivery quote covers every document: one parcel,
  // one courier rate, and the free-shipping threshold reads the lot.
  const basket = documentsFromQuote(quote.data);
  const firstItem = quote.data?.items[0];

  const signedIn = session.status === 'authenticated';
  const addresses = useAddresses(signedIn);

  const prices = useOptionPrices({
    uploadId: firstItem?.upload_id,
    copies: firstItem?.copies ?? 1,
    pincode: isPickup ? null : pincode,
    others: otherDocuments(basket, firstItem?.upload_id),
    fulfilmentMethod,
  });

  const priced = prices.data?.options.find(
    (entry) => entry.option.id === firstItem?.print_option_id,
  );
  const totalPaise = priced?.totals.total_paise ?? quote.data?.total_paise ?? null;

  // What "ready to continue" means differs: a delivery needs a serviceable
  // pincode, a collection needs nothing at all.
  const shippingKnown = Boolean(pincode) && priced?.shipping_status === 'resolved';
  // A delivery also needs to know where the door is. Required rather than
  // encouraged: without it the rider has the street and a phone number, which
  // is the difference between one attempt and three.
  const canContinue = isPickup || (shippingKnown && location !== null);

  useAnnounceOnChange(
    isPickup && totalPaise !== null
      ? `Collecting from the shop. Total ${formatPaise(totalPaise)}, with no delivery charge.`
      : shippingKnown && totalPaise !== null
        ? `Delivered total ${formatPaise(totalPaise)}, including delivery.`
        : null,
  );

  // Memoised, because AddressForm reports the PIN code from an effect: a fresh
  // function identity every render would make that effect fire forever.
  const handlePincodeChange = useCallback((next: string | null) => {
    setPincode(next);
  }, []);

  const handleLocationChange = useCallback((next: PickedLocation) => {
    setLocation(next);
  }, []);

  // Choosing a SAVED address adopts the pin it was saved with, so a returning
  // customer is not asked to place it again. An address saved before we
  // started asking has none, and the picker stays empty until they set one —
  // which is also what checkout would refuse them for.
  useEffect(() => {
    if (selectedAddressId === NEW_ADDRESS) {
      setLocation(null);
      return;
    }
    const chosen = addresses.data?.items.find((entry) => entry.id === selectedAddressId);
    if (chosen?.latitude != null && chosen.longitude != null) {
      setLocation({
        lat: Number(chosen.latitude),
        lng: Number(chosen.longitude),
        source: chosen.location_source === 'device' ? 'device' : 'map',
        accuracyM: chosen.location_accuracy_m ?? null,
      });
    } else {
      setLocation(null);
    }
  }, [addresses.data, selectedAddressId]);

  const submit = useApiMutation<SubmitResult, AddressFormValues>({
    mutationFn: async (values) => {
      // `canContinue` keeps the button disabled without one, so this is a
      // guard against a future edit rather than a case the customer can reach.
      // A real throw rather than a cast: if it ever does happen, an error is
      // better than an address saved with no pin on it.
      if (!location) {
        throw new Error('A delivery address needs a location before it can be saved.');
      }

      const existing = addresses.data?.items.find((entry) => entry.id === selectedAddressId);
      const address =
        existing ??
        (await createAddress({
          name: values.name,
          phone: values.phone,
          line1: values.line1,
          ...(values.line2 ? { line2: values.line2 } : {}),
          city: values.city,
          state: values.state,
          pincode: values.pincode,
          latitude: location.lat,
          longitude: location.lng,
          location_source: location.source,
          ...(location.accuracyM !== null ? { location_accuracy_m: location.accuracyM } : {}),
        }));

      // Re-quoted with the pincode, and with every document: delivery is priced
      // once for the whole parcel.
      const repriced = await createQuote({
        documents: basket,
        fulfilment_method: 'delivery',
        pincode: values.pincode,
      });

      return { quote: repriced, address };
    },
    onSuccess: ({ quote: repriced, address }) => {
      navigate(stepUrl('pay', { ...urlState, quote: repriced.id, address: address.id }));
    },
  });

  // A collection has nothing to fill in, so it gets its own submit: re-price
  // without a courier and go straight to payment.
  const collect = useApiMutation<QuoteOut, void>({
    mutationFn: () => createQuote({ documents: basket, fulfilment_method: 'pickup' }),
    onSuccess: (repriced) => {
      // The address id is dropped deliberately: a pickup has no address, and
      // carrying a stale one into checkout would have it refused.
      navigate(stepUrl('pay', { ...urlState, quote: repriced.id, address: undefined }));
    },
  });

  if (session.status === 'loading' || quote.isPending) {
    return (
      <AppShell title={step.title} progress={stepper}>
        <SkeletonGroup label="Loading your order">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </SkeletonGroup>
      </AppShell>
    );
  }

  if (!signedIn) {
    return (
      <AppShell
        title={step.title}
        progress={stepper}
        backTo={stepUrl('details', urlState)}
        backLabel="Back to your details"
      >
        <Alert severity="info" title="Confirm your number first">
          <p>
            We keep your delivery addresses against your account, so we need your mobile number
            before this step. Your file and your price are safe — they are in this page&rsquo;s
            address.
          </p>
          <div className="mt-3">
            <ButtonLink to={stepUrl('details', urlState)} fullWidth>
              Confirm my number
            </ButtonLink>
          </div>
        </Alert>
      </AppShell>
    );
  }

  if (quote.isError && quote.error) {
    return (
      <AppShell title={step.title} progress={stepper} backTo={stepUrl('options', urlState)}>
        <ErrorState
          error={quote.error}
          onRetry={() => void quote.refetch()}
          title="We could not load your price"
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      title={step.title}
      subtitle={
        isPickup
          ? 'Collect it from the shop. Nothing to fill in.'
          : 'Tell us the PIN code first — we will check delivery before asking for the rest.'
      }
      progress={stepper}
      backTo={stepUrl('details', urlState)}
      backLabel="Back to your details"
      action={
        <div className="flex flex-col gap-2">
          {totalPaise !== null ? (
            <p className="flex items-baseline justify-between text-sm">
              <span className="text-ink-muted">
                {isPickup
                  ? 'Total to pay'
                  : shippingKnown
                    ? 'Delivered total'
                    : 'Total before delivery'}
              </span>
              <span className="text-lg font-semibold tabular-nums text-ink">
                {formatPaise(totalPaise)}
              </span>
            </p>
          ) : null}
          {isPickup ? (
            <Button
              type="button"
              fullWidth
              onClick={() => collect.mutate()}
              loading={collect.isPending}
              loadingText="Saving your choice…"
            >
              Continue to payment
            </Button>
          ) : (
            <Button
              type="submit"
              form={FORM_ID}
              fullWidth
              disabled={!canContinue}
              loading={submit.isPending}
              loadingText="Saving your address…"
            >
              {!shippingKnown
                ? 'Enter a PIN code to continue'
                : location === null
                  ? 'Set your location to continue'
                  : 'Continue to payment'}
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {submit.isError && submit.error ? (
          <Alert severity="danger" title="We could not save that" live>
            <p>{submit.error.message}</p>
          </Alert>
        ) : null}

        {collect.isError && collect.error ? (
          <Alert severity="danger" title="We could not save that" live>
            <p>{collect.error.message}</p>
          </Alert>
        ) : null}

        {/* Before anything else on the screen: it decides whether there is an
            address to fill in at all, and whether delivery is being charged. */}
        <RadioGroup legend="How would you like to get it?">
          <OptionCard
            name="fulfilment"
            value="delivery"
            checked={!isPickup}
            onChange={() => setMethod('delivery')}
            label="Deliver to me"
            description="Posted to your address. Delivery is worked out from your PIN code."
          />
          <OptionCard
            name="fulfilment"
            value="pickup"
            checked={isPickup}
            onChange={() => setMethod('pickup')}
            label="Collect from the shop"
            description="No delivery charge. We will tell you when it is ready."
          />
        </RadioGroup>

        {isPickup ? (
          <Alert severity="info" title="We will tell you when it is ready">
            <p>
              Your order will be printed and held at the counter. You will see it move to
              &ldquo;Ready to collect&rdquo; on your orders page, and we will call you on the number
              on your account.
            </p>
          </Alert>
        ) : null}

        {isPickup ? null : (
          <AddressForm
            formId={FORM_ID}
            saved={addresses.data?.items ?? []}
            savedLoading={addresses.isLoading}
            selectedAddressId={selectedAddressId}
            onSelectAddress={setSelectedAddressId}
            onPincodeChange={handlePincodeChange}
            onValidSubmit={(values) => submit.mutate(values)}
            location={location}
            onLocationChange={handleLocationChange}
            serverFieldErrors={submit.error?.fieldErrors ?? {}}
            disabled={submit.isPending}
            defaultPhone={session.user?.phone ?? ''}
            defaultName={session.user?.name ?? ''}
          />
        )}

        {priced && canContinue ? (
          <>
            <Card title="What you will pay">
              <dl>
                <DataRow label="Printing" value={formatPaise(priced.totals.print_cost_paise)} />
                <DataRow
                  label="Delivery"
                  value={isPickup ? 'Not charged' : formatPaise(priced.totals.shipping_paise)}
                />
                <DataRow label="Tax" value={formatPaise(priced.totals.tax_paise)} />
                <DataRow
                  label={isPickup ? 'Total to pay' : 'Delivered total'}
                  value={formatPaise(priced.totals.total_paise)}
                  emphasis
                />
              </dl>
            </Card>
            <PriceBreakdown breakdown={priced.breakdown} totals={priced.totals} />
          </>
        ) : prices.isFetching ? (
          <SkeletonGroup label="Working out delivery">
            <Skeleton className="h-24 w-full" />
          </SkeletonGroup>
        ) : null}
      </div>
    </AppShell>
  );
}
